import React, { useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Box, IconButton, Tooltip, Typography, Button } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useTranslation } from 'react-i18next';

export default function TutorCameraFullscreen({ stream, cameraEnabled, audioBlocked, onEnableAudio }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [trackReady, setTrackReady] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const stageRef = useRef(null);
    const videoRef = useRef(null);
    const previousFocusRef = useRef(null);
    const closeRef = useRef(null);
    const nativeVideoFullscreenRef = useRef(false);
    const title = t('expandTutorCamera', { defaultValue: 'Expand teacher camera' });
    const closeTitle = t('exitTutorCamera', { defaultValue: 'Exit expanded camera' });
    const videoTrack = stream?.getVideoTracks?.().find(track => track.readyState === 'live');
    const cameraUnavailable = !cameraEnabled || !videoTrack;
    const cameraWaiting = !cameraUnavailable && (!trackReady || !videoReady);

    const restoreFocus = () => previousFocusRef.current?.focus?.();

    const close = () => {
        if (document.fullscreenElement === stageRef.current) {
            const exit = document.exitFullscreen?.();
            exit?.catch?.(() => {});
        }
        setExpanded(false);
        nativeVideoFullscreenRef.current = false;
        restoreFocus();
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!expanded || !video) return undefined;
        const track = stream?.getVideoTracks?.().find(item => item.readyState === 'live');
        const updateTrackState = () => setTrackReady(Boolean(track && track.readyState === 'live' && !track.muted));
        const handleNativeFullscreenEnd = () => {
            nativeVideoFullscreenRef.current = false;
            setExpanded(false);
            restoreFocus();
        };
        setVideoReady(false);
        updateTrackState();
        track?.addEventListener?.('mute', updateTrackState);
        track?.addEventListener?.('unmute', updateTrackState);
        track?.addEventListener?.('ended', updateTrackState);
        video.addEventListener('webkitendfullscreen', handleNativeFullscreenEnd);
        video.srcObject = stream || null;
        if (stream && cameraEnabled) video.play().catch(() => {});
        closeRef.current?.focus();
        return () => {
            track?.removeEventListener?.('mute', updateTrackState);
            track?.removeEventListener?.('unmute', updateTrackState);
            track?.removeEventListener?.('ended', updateTrackState);
            video.removeEventListener('webkitendfullscreen', handleNativeFullscreenEnd);
            video.srcObject = null;
        };
    }, [expanded, stream, cameraEnabled]);

    useEffect(() => {
        if (!expanded) return undefined;
        const handleKey = event => {
            if (event.key === 'Escape') {
                setExpanded(false);
                nativeVideoFullscreenRef.current = false;
                restoreFocus();
            }
            if (event.key === 'Tab') {
                const buttons = stageRef.current?.querySelectorAll('button:not(:disabled)');
                if (!buttons?.length) return;
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        const handleFullscreen = () => {
            if (!document.fullscreenElement) {
                setExpanded(false);
                restoreFocus();
            }
        };
        document.addEventListener('keydown', handleKey);
        document.addEventListener('fullscreenchange', handleFullscreen);
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.removeEventListener('fullscreenchange', handleFullscreen);
        };
    }, [expanded]);

    const expand = event => {
        event.stopPropagation();
        previousFocusRef.current = event.currentTarget;
        flushSync(() => setExpanded(true));

        const video = videoRef.current;
        if (cameraEnabled && stream && typeof video?.webkitEnterFullscreen === 'function') {
            try {
                video.srcObject = stream;
                video.muted = true;
                const playback = video.play?.();
                playback?.catch?.(() => {});
                video.webkitEnterFullscreen();
                nativeVideoFullscreenRef.current = true;
                return;
            } catch {
                nativeVideoFullscreenRef.current = false;
            }
        }

        // The in-page stage remains usable if native fullscreen is unavailable or denied.
        try {
            const request = stageRef.current?.requestFullscreen;
            const result = request?.call(stageRef.current);
            result?.catch?.(() => {});
        } catch {
            // Keep the expanded in-page stage on browsers that reject fullscreen.
        }
    };

    return <>
        <Tooltip title={title}>
            <IconButton
                aria-label={title}
                data-testid="expand-tutor-camera"
                onPointerDown={event => event.stopPropagation()}
                onClick={expand}
                sx={{ position: 'absolute', top: 4, right: 4, width: 44, height: 44,
                    pointerEvents: 'auto', zIndex: 2, bgcolor: 'background.paper', color: 'text.primary' }}
            ><FullscreenIcon /></IconButton>
        </Tooltip>
        {expanded && createPortal(
            <Box ref={stageRef} role="dialog" aria-modal="true" aria-label={title}
                data-testid="tutor-camera-expanded"
                onPointerDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}
                sx={{ position: 'fixed', inset: 0, zIndex: theme => theme.zIndex.modal + 100,
                    width: '100%', height: '100dvh', bgcolor: 'background.default', color: 'text.primary',
                    display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', pointerEvents: 'auto',
                    padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, minHeight: 48 }}>
                    <Typography sx={{ flex: 1 }}>{t('teacherCamera', { defaultValue: 'Teacher camera' })}</Typography>
                    {audioBlocked && <Button onClick={onEnableAudio}>{t('enableAudio', { defaultValue: 'Enable audio' })}</Button>}
                    <IconButton ref={closeRef} aria-label={closeTitle} onClick={close}
                        sx={{ width: 44, height: 44 }}><FullscreenExitIcon /></IconButton>
                </Box>
                <Box sx={{ position: 'relative', minHeight: 0 }}>
                    <video ref={videoRef} autoPlay playsInline muted
                        onLoadedData={() => setVideoReady(true)}
                        onPlaying={() => setVideoReady(true)}
                        onWaiting={() => setVideoReady(false)}
                        onStalled={() => setVideoReady(false)}
                        style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
                    {(cameraUnavailable || cameraWaiting) &&
                        <Box role="status" sx={{ position: 'absolute', inset: 0, display: 'grid',
                            placeItems: 'center', bgcolor: 'background.default' }}>
                            {cameraUnavailable
                                ? t('cameraUnavailable', { defaultValue: 'Teacher camera is unavailable' })
                                : t('cameraConnecting', { defaultValue: 'Waiting for teacher video' })}
                        </Box>}
                </Box>
            </Box>, document.body,
        )}
    </>;
}
